<template>
    <div class="container-fluid overflow-auto px-0">
        <div class="row g-0 min-vh-100">
            <!-- LEFT SIDE (IMAGE / ILLUSTRATION) -->
            <div
                class="col-lg-8 d-none d-lg-flex align-items-center justify-content-center bg-light"
                :style="{
                    backgroundImage: `url(${bldgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center center',
                }"
            ></div>

            <!-- RIGHT SIDE (FORM) -->
            <div
                class="col-lg-4 col-12 d-flex align-items-center justify-content-center"
            >
                <div class="w-100" style="max-width: 420px">
                    <div
                        class="d-flex align-items-center justify-content-center d-md-flex d-lg-none"
                    >
                        <img
                            :src="logoOnlySrc"
                            alt="Tenny Enterprises, Inc. Logo"
                            class="text-center"
                            height="50"
                        />
                    </div>
                    <div class="card my-4 border-0">
                        <!-- HEADER -->
                        <div
                            class="d-flex justify-content-between align-items-center"
                        >
                            <p
                                class="text-start text-muted mb-0 back-btn"
                                v-on:click="backToLogin"
                                role="button"
                                v-if="isVerifyOTP"
                            >
                                <font-awesome-icon icon="arrow-left-long" />
                                Back to Login
                            </p>
                        </div>

                        <!-- SELECTION -->
                        <div
                            class="text-start d-flex flex-column gap-3"
                            v-if="asAccount == null"
                        >
                            <div class="text-start">
                                <h5 class="greet">Welcome</h5>
                                <p class="greet-subtitle">
                                    Register or login to continue.
                                </p>
                            </div>

                            <div
                                class="border p-4 button-selection"
                                @click="loginAsAccount(true)"
                                role="button"
                            >
                                <div
                                    class="d-flex align-items-center justify-content-between"
                                >
                                    <div class="d-flex flex-column">
                                        <h5
                                            class="fw-semibold button-selection-title"
                                        >
                                            Login as Customer Account
                                        </h5>
                                        <p
                                            class="text-gray-600 mb-0 button-selection-subtitle"
                                        >
                                            For exclusive customers.
                                        </p>
                                    </div>
                                    <font-awesome-icon
                                        class="arrow-right"
                                        icon="arrow-right"
                                    />
                                </div>
                            </div>

                            <div
                                class="border p-4 button-selection"
                                @click="loginAsAccount(false)"
                                role="button"
                            >
                                <div
                                    class="d-flex align-items-center justify-content-between"
                                >
                                    <div class="d-flex flex-column">
                                        <h5
                                            class="fw-semibold button-selection-title"
                                        >
                                            Login as Guest
                                        </h5>
                                        <p
                                            class="text-gray-600 mb-0 button-selection-subtitle"
                                        >
                                            For retail and new customers.
                                        </p>
                                    </div>
                                    <font-awesome-icon
                                        class="arrow-right"
                                        icon="arrow-right"
                                    />
                                </div>
                            </div>

                            <div class="text-start mt-2">
                                <p class="no-account-label">
                                    Don't have an account?
                                </p>
                                <div class="d-flex flex-column gap-4">
                                    <router-link
                                        to="/register"
                                        class="btn w-100 btn-primary button-create-account"
                                    >
                                        Create an account
                                    </router-link>
                                    <router-link
                                        to="/"
                                        class="btn btn-back w-100"
                                    >
                                        Back
                                    </router-link>
                                </div>
                            </div>
                        </div>

                        <!-- CUSTOMER LOGIN -->
                        <div v-if="asAccount">
                            <div class="text-start mb-4">
                                <h5 class="greet">Customer Account Login</h5>
                                <p class="greet-subtitle">
                                    Register or login to continue.
                                </p>
                            </div>
                            <Alert class="mx-0" ref="alert"></Alert>
                            <form @submit.prevent="login">
                                <div class="mb-3">
                                    <label
                                        for="accountId"
                                        class="form-label input-field-label"
                                        >Account ID</label
                                    >
                                    <input
                                        v-model="data.account_id"
                                        id="accountId"
                                        class="form-control input-field"
                                        required
                                    />
                                </div>
                                <div class="mb-3">
                                    <label
                                        for="username"
                                        class="form-label input-field-label"
                                        >Username</label
                                    >
                                    <input
                                        v-model="data.user_name"
                                        id="username"
                                        class="form-control input-field"
                                        required
                                    />
                                </div>

                                <div class="mb-3">
                                    <label
                                        for="password"
                                        class="form-label input-field-label"
                                        >Password</label
                                    >
                                    <div class="position-relative">
                                        <input
                                            v-model="data.password"
                                            :type="passwordFieldType"
                                            id="password"
                                            class="form-control input-field"
                                            required
                                        />
                                        <font-awesome-icon
                                            :icon="['far', eyeIcon]"
                                            class="log"
                                            @click="togglePasswordVisibility"
                                        />
                                    </div>
                                </div>

                                <div class="text-end mb-3">
                                    <router-link to="/forgot-password">
                                        Forgot Password?
                                    </router-link>
                                </div>
                                <div class="d-flex flex-column gap-4">
                                    <button
                                        type="submit"
                                        class="btn btn-primary w-100"
                                    >
                                        Login
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-back w-100"
                                        @click="asAccount = null"
                                    >
                                        Back
                                    </button>
                                </div>
                            </form>
                        </div>

                        <!-- GUEST LOGIN -->
                        <div v-if="asAccount == false">
                            <div class="text-start mb-4">
                                <h5 class="greet">Guest Login</h5>
                                <p class="greet-subtitle">
                                    Login with your credentials.
                                </p>
                            </div>
                            <Alert class="mx-0" ref="alert"></Alert>
                            <form @submit.prevent="login">
                                <div class="mb-3">
                                    <label
                                        for="email"
                                        class="form-label input-field-label"
                                        >Email</label
                                    >
                                    <input
                                        v-model="data.email"
                                        id="email"
                                        class="form-control input-field mb-3"
                                        placeholder="Email"
                                        required
                                    />
                                </div>
                                <div class="mb-3">
                                    <label
                                        for="password"
                                        class="form-label input-field-label"
                                        >Password</label
                                    >
                                    <div class="position-relative">
                                        <input
                                            v-model="data.password"
                                            :type="passwordFieldType"
                                            id="password"
                                            class="form-control input-field"
                                            placeholder="Password"
                                            required
                                        />
                                        <font-awesome-icon
                                            :icon="['far', eyeIcon]"
                                            class="log"
                                            @click="togglePasswordVisibility"
                                        />
                                    </div>
                                </div>
                                <div class="text-end mb-3">
                                    <router-link to="/forgot-password">
                                        Forgot Password?
                                    </router-link>
                                </div>
                                <div class="d-flex flex-column gap-4">
                                    <button
                                        type="submit"
                                        class="btn btn-primary w-100"
                                    >
                                        Login
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-back w-100"
                                        @click="asAccount = null"
                                    >
                                        Back
                                    </button>
                                </div>
                                <div class="divider">
                                    <span>or</span>
                                </div>
                                <div
                                    class="social-auth d-flex align-items-center justify-content-center gap-4"
                                >
                                    <div
                                        class="d-flex flex-column align-items-center gap-2 google-auth"
                                    >
                                        <button
                                            type="button"
                                            class="btn d-flex align-items-center justify-content-center button-social-auth button-google-auth"
                                        >
                                            <font-awesome-icon
                                                :icon="['fab', 'google']"
                                            />
                                        </button>
                                        <p class="button-google-auth-label">
                                            Google
                                        </p>
                                    </div>
                                    <div
                                        class="d-flex flex-column align-items-center gap-2 facebook-auth"
                                    >
                                        <button
                                            type="button"
                                            class="btn d-flex align-items-center justify-content-center button-social-auth button-facebook-auth"
                                        >
                                            <font-awesome-icon
                                                :icon="['fab', 'facebook']"
                                            />
                                        </button>
                                        <p class="button-facebook-auth-label">
                                            Facebook
                                        </p>
                                    </div>
                                    <div
                                        class="d-flex flex-column align-items-center gap-2 apple-auth"
                                    >
                                        <button
                                            type="button"
                                            class="btn d-flex align-items-center justify-content-center button-social-auth button-apple-auth"
                                        >
                                            <font-awesome-icon
                                                :icon="['fab', 'apple']"
                                            />
                                        </button>
                                        <p class="button-apple-auth-label">
                                            Apple
                                        </p>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <div class="text-muted my-5 position-fixed bottom-0">
                    <p class="m-0">© 2024 Tenny Enterprises Inc.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script src="./LoginScript.vue" />
